import { AutocompleteColorSearch, BreedSearchAutocomplete } from "../../../components";
import { Box, Button, ClickAwayListener, Typography, styled } from "@mui/material";
import {
  DatePickerController,
  GvInputController,
  GvSelectController,
} from "../../../components/Controllers";
import { LIST_GENDERS, YEARS_PAST_PET } from "../../../config/params";
import { PetUpdateFormType, PetUpdateSchema } from "../../../services/pet-update/pet-update.schema";
/* eslint-disable quote-props */
import React, { FC, useEffect, useState } from "react";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import { fromUnixToDate, subtractYearsToDate } from "../../../utils";

import { IOptionGeneric } from "../../interface";
import { IPetDetailResponse } from "../../../services/pet-detail/pet-detail.interface";
import { InfoIcon } from "../../../components/Icons";
import { paletteColors } from "../../../styles/muiTheme";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

interface IEditPetProfileFormProps {
  onSubmit: (data: PetUpdateFormType) => void;
  onCancel?: () => void;
  pet: IPetDetailResponse | undefined;
}

const EditPetProfileForm: FC<IEditPetProfileFormProps> = ({ onSubmit, onCancel, pet }) => {
  const [open, setOpen] = useState(false);
  const [breedName, setBreedName] = useState<string>("");
  const [colorName, setColorName] = useState<string>("");
  const { control, handleSubmit, watch, setValue } = useForm<PetUpdateFormType>({
    mode: "onSubmit",
    resolver: yupResolver(PetUpdateSchema),
    defaultValues: {
      name: "",
      species: "",
      birthdate: null,
      breed: "",
      gender: "",
      idColor: "",
      isDesexed: "",
    },
  });

  const watchSpecies = watch("species", "");

  const handleChangeColor = (text: string) => {
    if (!text) {
      setValue("idColor", "", { shouldValidate: true });
      setColorName("");
    }
  };

  const handleSelectColor = (color: IOptionGeneric) => {
    setValue("idColor", color.value, { shouldValidate: true });
    setColorName(color.label);
  };

  useEffect(() => {
    if (pet) {
      const birthdate = pet.birthdate ? fromUnixToDate(+pet.birthdate) : null;

      if (pet.species) {
        setValue("species", pet.species, { shouldValidate: true });
      }
      setValue("gender", pet.gender || "", { shouldValidate: true });
      setValue("name", pet.name, { shouldValidate: true });
      setValue("birthdate", birthdate, { shouldValidate: true });
      setValue("breed", pet.breed || "", { shouldValidate: true });
      setValue("idColor", pet.idColor || "", { shouldValidate: true });

      if (pet.isDesexed) {
        setValue("isDesexed", "1");
      } else {
        setValue("isDesexed", "0");
      }

      if (pet.breedName) {
        setBreedName(pet.breedName);
      }

      // setGenderName(LIST_GENDERS.find((gender)
      //  => gender.value === pet.gender)?.label || "");
      // Might hasve use case for this later

      if (pet.color) {
        setColorName(pet.color);
      }
    }
  }, [pet]);

  const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: paletteColors.sand.main,
      color: paletteColors.black.main,
      fontSize: "12px",
      fontWeight: 400,
      lineHeight: "16px",
      fontFamily: "geograph-regular",
      padding: ".5rem 1rem",
      border: `1px solid ${paletteColors.taupe.dark}`,
      top: ".5rem",
      left: "-1.5rem",
      position: "absolute",
      width: "265px",
      p: {
        fontSize: "12px",
      },
    },
  });

  return <>
    <form onSubmit={handleSubmit(onSubmit)}>
      <GvInputController
        control={control}
        name="name"
        label="Name"
        placeholder="Add a name"
        className="font-semibold"
      />

      <DatePickerController
        control={control}
        name="birthdate"
        label="Birthday"
        disableFuture={true}
        minDate={subtractYearsToDate(new Date(), YEARS_PAST_PET)}
        maxDate={new Date()}
        edit
        className="font-semibold"
      />

      {watchSpecies !== "3" ? (
        <BreedSearchAutocomplete
          id="breedAutocomplete"
          name="breed"
          control={control}
          specieId={watchSpecies}
          text={breedName}
          placeholder="No bread selected"
          disabled
          tooltip="Please let your veterinarian know if your pet's breed or gender is listed incorrectly."
          className="font-semibold"
        />
      ) : null}

      <Box sx={{ display: "flex" }}>
        <Typography sx={{ mb: 1 }} className="font-semibold">Gender</Typography>
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <HtmlTooltip
            title={
              <Typography color="inherit">
                Please let your veterinarian know if your pet&apos;s breed or gender is listed incorrectly.
              </Typography>
            }
            placement="right"
            open={open}
          >
            <Box
              sx={{ display: "grid", ml: .5 }}
              onMouseEnter={() => setOpen(true)}
              onMouseLeave={() => setOpen(false)}
              onTouchStart={() => setOpen(true)}
            >
              <InfoIcon width={18} height={18} />
            </Box>
          </HtmlTooltip>
        </ClickAwayListener>
      </Box>

      <GvSelectController
        control={control}
        name="gender"
        options={LIST_GENDERS}
        disabled
        className="font-semibold"
      />

      <AutocompleteColorSearch
        id="colorAutocomplete"
        onSelect={handleSelectColor}
        onChange={handleChangeColor}
        text={colorName}
        className="font-semibold"
      />

      <GvSelectController
        control={control}
        name="isDesexed"
        label={"My pet is neutered"}
        options={[
          { value: "1", label: "Yes" },
          { value: "0", label: "No" },
        ]}
        disabled
        className="!font-semibold"
      />

      <Box >
        <Button
          fullWidth
          id="petEditFormSubmit"
          color="primary"
          type="submit"
          className="!capitalize !mb-4"
        >
          Save
        </Button>
      </Box>
    </form>
  </>;
};

export default EditPetProfileForm;
